import { AppStartupService, BaseComponent } from '@ridango/core';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import Env from '@ridango/core/env';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationFacade, PurchaseService, SessionService, ShoppingCartService } from '@ridango/components';
import { ModalController, Platform } from '@ionic/angular';
import {
    CartTicketRequest,
    CategoryProduct,
    Product,
    ProductActivationMethod,
    ActivationType,
    Ticket,
    ZoneInterface
} from '@ridango/components/lib/purchase/purchase.model';
import { JourneyRepurchaseData } from '@ridango/components';
import { switchMap, takeUntil, tap, map, take } from 'rxjs/operators';
import { TicketPurchaseSameValidityConfirmationComponent } from '../purchase-page-v2/ticket-purchase-same-validity-confirmation/ticket-purchase-same-validity-confirmation.component';
import { HelperService } from '../../shared/services/helper.service';
import { Observable, of } from 'rxjs';

@Component({
    templateUrl: 'journey-planner-purchase-tickets-page.component.html',
    styleUrls: ['journey-planner-purchase-tickets-page.component.scss']
})
export class JourneyPlannerPurchaseTicketsPageComponent extends BaseComponent implements OnInit, OnDestroy {
    public categoryProducts: CategoryProduct[];
    urls: string[] = ['journey-planner', 'purchase'];
    currency = this.appStartupService.getCurrency();

    constructor(
        @Inject('ENV') public env: Env,
        private router: Router,
        private translate: TranslateService,
        private activatedRoute: ActivatedRoute,
        private applicationFacade: ApplicationFacade,
        private purchaseService: PurchaseService,
        private shoppingCartService: ShoppingCartService,
        private sessionService: SessionService,
        private platform: Platform,
        private modalCtrl: ModalController,
        public appStartupService: AppStartupService,
        private helperService: HelperService
    ) {
        super();
    }

    assetsPath$ = this.applicationFacade.assetsPath$;
    isLoading$ = this.purchaseService.isPurchaseDataLoading$;
    steps: string[] = ['category-product', 'product', 'ticket-activation', 'cart'];
    selectedActivationMethod: string;
    activeStep = 0;
    stepFromUrl: string;
    continueToCheckoutCurrency: string;
    priceZoneList: string;
    physicalBackButtonSubscription$;
    zone: ZoneInterface = {
        origin: null,
        destination: null
    };
    private repurchaseData: JourneyRepurchaseData | null;
    private hasInitiatedRepurchaseNavigation = false;
    zones = 0;
    duration = 0;
    readonly productsLocalStorageKey = this.env.APP_NAME + '-products-to-purchase';

    ngOnInit(): void {
        if (!this.env.PURCHASE_FLOW?.TICKET_STEPS?.includes('ticket-activation')) {
            this.steps = this.steps.filter((step) => step !== 'ticket-activation');
        }

        this.physicalBackButtonSubscription$ = this.platform.backButton.subscribeWithPriority(1000, () => {
            this.handleBackClicked();
        });
        const queryParams = this.sessionService.getJourneyQueryParams();
        if (queryParams) {
            const params = new URLSearchParams(decodeURIComponent(queryParams));

            const obj = {};

            params.forEach((value, key) => {
                obj[key] = JSON.parse(value);
            });
            this.zone.origin = obj?.['origin'];
            this.zone.destination = obj?.['destination'];
            this.duration = (obj?.['duration'] || 0) / 3600;
            this.priceZoneList = obj?.['priceZoneList'] || '';
            this.zones = obj?.['priceZoneList']?.split(',').length || 0;
        }
        this.repurchaseData = this.sessionService.getJourneyRepurchaseData();

        this.purchaseService
            .getJourneyPurchaseProducts(this.priceZoneList)
            .pipe(
                takeUntil(this.componentDestroyed$),
                tap((categoryProducts: CategoryProduct[]) => {
                    const currency = categoryProducts[0]?.products[0]?.price?.currency;

                    if (currency) {
                        this.continueToCheckoutCurrency = currency;
                    }
                }),
                switchMap((categoryProducts: CategoryProduct[]) => {
                    this.categoryProducts = categoryProducts
                        .map((categoryProduct) => ({
                            ...categoryProduct,
                            products: categoryProduct.products?.filter(
                                (product) => product.price && (product.price?.amount > 0 || product.price.maxAmount > 0)
                            )
                        }))
                        .filter((categoryProduct) => categoryProduct.products.length > 0);

                    return this.activatedRoute.url;
                })
            )
            .subscribe((segments) => {
                if (segments.length === 0 || segments[0].path === '**') {
                    this.router.navigate([this.translate.currentLang, ...this.urls, this.steps[0]], {
                        skipLocationChange: true
                    });
                }

                const stepsFromUrl = segments
                    .filter((segment) => this.steps.includes(segment.path))
                    .map((segment) => segment.path);
                const newStepFromUrl = stepsFromUrl[stepsFromUrl.length - 1];
                const newActiveStep = this.steps.indexOf(newStepFromUrl);

                this.stepFromUrl = newStepFromUrl;
                this.activeStep = newActiveStep;
                this.purchaseService.setPurchaseDataLoading(false);
                this.tryAutoRepurchase();
            });

        this.purchaseService.activationMethod$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((activationMethod) => {
                this.selectedActivationMethod = activationMethod;
            });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.physicalBackButtonSubscription$?.unsubscribe();
    }

    get shouldShowCloseButton(): boolean {
        return !this.env.PURCHASE_FLOW?.HIDE_CLOSE_BUTTON;
    }

    handleClosePurchasePage(): void {
        this.router.navigate([this.translate.currentLang, 'journey-planner']);
    }

    handleBackClicked(): void {
        this.purchaseService.setPurchaseDataLoading(true);
        if (this.steps.indexOf(this.stepFromUrl) === 0 || !this.stepFromUrl) {
            const params = this.sessionService.getJourneyQueryParams();
            const route = `${this.translate.currentLang}/journey-planner/result${params ? '?' + params : ''}`;

            this.router.navigateByUrl(route, { replaceUrl: true }).then();
            return;
        }
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        const prevStep = this.steps[this.steps.indexOf(this.stepFromUrl) - 1];

        const prevStepIndexInSegments = urlSegments.indexOf(prevStep);

        urlSegments.splice(prevStepIndexInSegments + 1);
        this.router.navigate([this.translate.currentLang, ...this.urls, ...urlSegments], { replaceUrl: true });
    }

    handleNextClicked(): void {
        this.purchaseService.setPurchaseDataLoading(true);
        if (this.steps.indexOf(this.stepFromUrl) === this.steps.length - 2) {
            this.prePurchaseCheckTickets(() => {
                this.goToCart();
            });
            return;
        }
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        urlSegments.push(this.steps[this.steps.indexOf(this.stepFromUrl) + 1]);
        this.router.navigate([this.translate.currentLang, ...this.urls, ...urlSegments], { replaceUrl: true });
    }

    prePurchaseCheckRequired(): boolean {
        const currentActivationModeInUrl = this.selectedActivationMethod;
        return (
            currentActivationModeInUrl !== 'MANUAL' /*( MANUAL = "Later" in UI) */ &&
            this.env.PURCHASE_FLOW.ACTIVATION_CHECK_VALIDITY
        );
    }

    getPrePurchaseCheckTicketParametersCode(selectedTravellerTicket, selectedToday) {
        const parametersCodeNow = 'PURCHASE'; //means NOW, during purchase

        const activationMethodToCheckWith = selectedToday
            ? parametersCodeNow //when selected day is today hardcode set activation parameter to PURCHASE(as datetime=now) in check to prevent SPECIFIED_ACTIVATION_OUT_OF_BOUNDS_PAST (timestamp) AFC error on check
            : this.selectedActivationMethod;

        const availableActivationParameters =
            selectedTravellerTicket.productParameters?.availableParameters?.activations;
        const pdpParametersCode = selectedTravellerTicket.productParameters.parameterizedDerivedProduct?.parametersCode;
        return !availableActivationParameters &&
            selectedTravellerTicket.activationMethod.length === 1 &&
            pdpParametersCode
            ? pdpParametersCode
            : availableActivationParameters.find((el) => el.activationTrigger === activationMethodToCheckWith)
                  ?.parametersCode;
    }

    private getProductNextSegment(): string | null {
        const segments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        const index = segments.findIndex((segment) => segment === 'product');
        return index !== -1 ? segments[index + 1] ?? null : null;
    }

    private getUrlProductIdWithTickets(localTickets: Product[] | null): string | null {
        if (localTickets?.length) {
            return null;
        }
        const next = this.getProductNextSegment();
        return next ? next.split(':')[0] : null;
    }

    prePurchaseCheckTickets(callback) {
        if (!this.prePurchaseCheckRequired()) {
            callback();
            return;
        }

        const localTickets = JSON.parse(localStorage.getItem(this.productsLocalStorageKey));

        const activationDateParams = this.purchaseService.getPrePurchaseCheckActivationDateParams(
            this.selectedActivationMethod
        );

        const urlProductId = this.getUrlProductIdWithTickets(localTickets);

        this.getTicketsToCheck$(localTickets, urlProductId)
            .pipe(take(1))
            .subscribe((tickets) => {
                const ticketsToPrePurchaseCheck = this.mapTicketsToPrePurchaseCheck(tickets, activationDateParams);

                this.shoppingCartService
                    .prePurchaseCheck({
                        tickets: ticketsToPrePurchaseCheck
                    })
                    .subscribe((result) => {
                        this.handlePrePurchaseCheckResult(result, callback);
                    });
            });
    }

    private getTicketsToCheck$(localTickets: any[] | null, urlProductId: string | null): Observable<any[]> {
        if (localTickets?.length) {
            return of(localTickets);
        }
        return this.purchaseService
            .getProducts([this.getCurrentAreaValue()])
            .pipe(map((products) => this.buildTicketsFromUrl(products, urlProductId)));
    }

    private buildTicketsFromUrl(products: any[], urlProductId: string | null): any[] {
        const productFromUrl = products.find((p) => p.id === urlProductId);

        if (!productFromUrl) {
            return [];
        }

        const selectionsInUrl = this.purchaseService.getStepDataIdsByStepName('product');
        const extractedParams = this.extractParamsFromUrl(selectionsInUrl, urlProductId);
        const hasDynamicParams = Object.keys(extractedParams).length > 0;
        const idx = selectionsInUrl.indexOf(productFromUrl.id);
        const amount = !hasDynamicParams && idx !== -1 ? selectionsInUrl[idx + 1] || 1 : 1;

        return [
            {
                id: productFromUrl.id,
                amount: amount,
                productParameters: productFromUrl.productParameters,
                activationMethod: productFromUrl.activationMethod,
                dynamicProductParameters: hasDynamicParams ? extractedParams : productFromUrl.dynamicProductParameters
            }
        ];
    }

    private extractParamsFromUrl(
        selectionsInUrl: (string | number)[],
        urlProductId: string | null
    ): { [key: string]: number } {
        const extractedParams: { [key: string]: number } = {};
        selectionsInUrl.forEach((seg: string | number, i: number) => {
            if (typeof seg === 'string' && seg.startsWith(`${urlProductId}:`)) {
                const key = seg.split(':')[1];
                const val = selectionsInUrl[i + 1];
                if (key && val && !isNaN(Number(val))) {
                    extractedParams[key] = Number(val);
                }
            }
        });
        return extractedParams;
    }

    private mapTicketsToPrePurchaseCheck(tickets: any[], activationDateParams: any): any {
        return tickets.map((selectedTravellerTicket) => {
            const hasDynamicParams =
                selectedTravellerTicket.dynamicProductParameters &&
                Object.keys(selectedTravellerTicket.dynamicProductParameters).length > 0;

            return {
                productId: selectedTravellerTicket.id,
                parametersCode: this.getPrePurchaseCheckTicketParametersCode(
                    selectedTravellerTicket,
                    activationDateParams.isToday
                ),
                quantity: selectedTravellerTicket.amount,
                activationDate: this.helperService.appDateTimeToServerDateTime(activationDateParams.date).toString(),
                ...(hasDynamicParams && {
                    dynamicProductParameters: selectedTravellerTicket.dynamicProductParameters
                })
            };
        });
    }

    private handlePrePurchaseCheckResult(result: any, callback: () => void): void {
        const sameValidityFound = !result.checkPassed;
        if (sameValidityFound) {
            this.showSameValidityConfirmationModal(callback);
        } else {
            callback();
        }
    }

    private async showSameValidityConfirmationModal(callback: () => void): Promise<void> {
        this.purchaseService.setPurchaseDataLoading(false);
        const modal = await this.modalCtrl.create({
            component: TicketPurchaseSameValidityConfirmationComponent,
            cssClass: 'ticket-purchase-same-validity-confirmation-modal'
        });

        await modal.present();

        const { data: result } = await modal.onWillDismiss<boolean>();
        if (result === true) {
            this.purchaseService.setPurchaseDataLoading(true);
            callback();
        }
    }

    getCurrentAreaValue() {
        return this.activatedRoute.snapshot.url[
            this.activatedRoute.snapshot.url.findIndex((segment) => segment?.path === 'product-area') + 1
        ]?.path.toString();
    }

    private goToCart() {
        this.purchaseService.setPurchaseDataLoading(true);
        const localTickets = this.purchaseService.getProductAreasLocalStorageKey;
        const cartID = this.shoppingCartService.getCartId();

        const tickets: CartTicketRequest[] = localTickets
            ? localTickets
                  .map((ticket) => {
                      let parametersCode: string;

                      if (ticket?.productParameters && ticket.productParameters?.availableParameters?.activations) {
                          parametersCode = ticket.productParameters.availableParameters.activations.find(
                              (el) => el.activationTrigger === this.selectedActivationMethod
                          )?.parametersCode;
                      }

                      return Array(ticket.amount).fill({
                          productId: ticket.id,
                          dynamicProductParameters: ticket.dynamicProductParameters || {},
                          email: this.sessionService.getLoggedInEmail(),
                          startZoneId: ticket.startZoneId,
                          endZoneId: ticket.endZoneId,
                          parametersCode
                      });
                  })
                  .reduce((acc, val) => acc.concat(val), [])
            : [];

        if (this.env.IS_NEW_AFC && tickets.length === 0) {
            const segments = this.activatedRoute.snapshot.url;
            const pIdx = segments.findIndex((u) => u.path === 'product');
            const pId = segments[pIdx + 1]?.path?.split(':')[0];

            if (pId) {
                const dynamicParams: { [key: string]: number } = {};
                segments.forEach((s, i) => {
                    if (s.path.startsWith(`${pId}:`)) {
                        const k = s.path.split(':')[1];
                        const v = segments[i + 1]?.path;
                        if (k && v && !isNaN(Number(v))) dynamicParams[k] = Number(v);
                    }
                });

                const urlAmount = Number(segments[pIdx + 2]?.path);
                const amount = Object.keys(dynamicParams).length > 0 || isNaN(urlAmount) ? 1 : urlAmount;

                for (let i = 0; i < amount; i++) {
                    tickets.push({
                        productId: pId,
                        dynamicProductParameters: dynamicParams,
                        email: this.sessionService.getLoggedInEmail(),
                        startZoneId: null,
                        endZoneId: null,
                        parametersCode: null
                    });
                }
            }
        }
        if (this.env.IS_NEW_AFC && cartID) {
            this.shoppingCartService.getShoppingCartStatus(cartID).subscribe((currentCart) => {
                currentCart.tickets.forEach((ticket: Ticket) => {
                    const mappedTicket: Pick<Product, 'productId' | 'parametersCode' | 'dynamicProductParameters'> = {
                        productId: ticket.productId,
                        dynamicProductParameters: ticket.dynamicProductParameters || {}
                    };
                    mappedTicket.parametersCode = ticket.parametersCode;

                    tickets.push(mappedTicket);
                });
                this.addProductToCart(cartID, tickets);
            });
        } else {
            this.addProductToCart(cartID || null, tickets);
        }
    }

    private tryAutoRepurchase(): void {
        // Exit early if there is no repurchase context or navigation is already in progress.
        if (
            this.hasInitiatedRepurchaseNavigation ||
            !this.repurchaseData?.productId ||
            !this.categoryProducts?.length ||
            !this.stepFromUrl
        ) {
            return;
        }

        // Extract repurchase data saved from the previous Journey Planner flow.
        const { productId, productParametersCode, dynamicProductParameters, startZoneId, endZoneId, activations } =
            this.repurchaseData;

        // Find the category that contains the product we want to re-purchase.
        const categoryProduct = this.categoryProducts.find((cp) => cp.products?.some((p) => p.id === productId));
        if (!categoryProduct) {
            this.sessionService.removeJourneyRepurchaseData();
            return;
        }

        // Find the actual product within that category.
        const product = categoryProduct.products.find((p) => p.id === productId);
        if (!product) {
            this.sessionService.removeJourneyRepurchaseData();
            return;
        }

        // Map stored activation data from ticket history into the format expected by the purchase flow.
        const mappedActivations: ProductActivationMethod[] =
            activations?.map((a) => {
                const safeDescription =
                    a && typeof a === 'object' && 'description' in a && a.description?.translations
                        ? a.description
                        : { translations: [] };

                return {
                    activationTrigger: a.activationTrigger,
                    name: a?.name ?? { translations: [] },
                    description: safeDescription
                };
            }) || [];

        // Prefer activations from repurchase data; fall back to product defaults if none exist.
        const activationMethod: ProductActivationMethod[] =
            mappedActivations.length > 0 ? mappedActivations : product.activationMethod || [];

        // Prepare a single selected product instance with zones, parameters and activations restored.
        const productWithSelection = {
            ...product,
            amount: 1,
            startZoneId: startZoneId ?? product.startZoneId,
            endZoneId: endZoneId ?? product.endZoneId,
            dynamicProductParameters: dynamicProductParameters ?? product.dynamicProductParameters,
            parametersCode: productParametersCode ?? product.parametersCode,
            activationMethod
        };

        // Seed the local purchase state so the rest of the flow behaves as if the user selected this product manually.
        this.purchaseService.setArrayLocalProducts([productWithSelection]);

        // Determine available activation triggers for this product and select a default one.
        const triggers: ActivationType[] = activationMethod
            .map((m) => m.activationTrigger as ActivationType)
            .filter(Boolean) as ActivationType[];
        const selectedTrigger: ActivationType = triggers[0] || ActivationType.AFTER_PURCHASE;
        const hasMultipleActivation = triggers.length > 1;
        this.purchaseService.emitSelectActivationMethod(selectedTrigger);

        const defaultQuantity = 1;
        // Build the navigation URL back into the Journey Planner purchase steps.
        const navigationSegments = [
            this.translate.currentLang,
            ...this.urls,
            'category-product',
            categoryProduct.id,
            'product',
            product.id,
            defaultQuantity
        ];

        // Prevent multiple navigations and clear repurchase state after it has been consumed.
        this.hasInitiatedRepurchaseNavigation = true;
        this.sessionService.removeJourneyRepurchaseData();
        if (hasMultipleActivation && this.steps.includes('ticket-activation')) {
            // If there are multiple activation options, send the user to the activation selection step.
            navigationSegments.push('ticket-activation', selectedTrigger);
            this.router.navigate(navigationSegments, { replaceUrl: true });
        } else {
            // Otherwise skip activation selection and go directly to the cart.
            this.goToCart();
        }
    }

    private shouldBackfillTicketStarting(
        ticket: CartTicketRequest,
        activationDateParams: ReturnType<PurchaseService['getPrePurchaseCheckActivationDateParams']>
    ): boolean {
        if (this.selectedActivationMethod === ActivationType.MANUAL) {
            return false;
        }

        const ticketProduct = this.categoryProducts.find((cp) => cp.products?.some((p) => p.id === ticket.productId));
        if (ticketProduct && this.purchaseService.isDynamicLengthProduct(ticketProduct) && ticket.starting) {
            return false;
        }

        if (this.env.IS_NEW_AFC) {
            return ticket.starting == null;
        }

        return !activationDateParams.isToday;
    }

    private mapTicketsData(tickets: CartTicketRequest[]): CartTicketRequest[] {
        const activationDateParams = this.purchaseService.getPrePurchaseCheckActivationDateParams(
            this.selectedActivationMethod
        );

        return tickets.map((ticket) => {
            const mapped = { ...ticket };

            if (this.selectedActivationMethod === ActivationType.MANUAL) {
                mapped.starting = null;
                return mapped;
            }

            if (this.shouldBackfillTicketStarting(ticket, activationDateParams)) {
                mapped.starting = this.helperService.appDateTimeToServerDateTime(activationDateParams.date).toString();
            }

            return mapped;
        });
    }

    private addProductToCart(shoppingCartId: number | null, tickets: CartTicketRequest[]) {
        tickets = this.mapTicketsData(tickets);
        this.shoppingCartService
            .addProductToCartV2({ shoppingCartId, tickets })
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                () => {
                    this.purchaseService.resetProductsLocalStorage();
                    this.router.navigate([this.translate.currentLang, 'purchase-v2', 'cart']);
                },
                (error) => {
                    if (error.status === 409) {
                        this.addProductToCart(null, tickets);
                    }
                }
            );
    }

    getTotalAmount() {
        return this.purchaseService
            .getLocalProductAmount()
            .filter((product) => product.amount > 0)
            .map((product) => {
                this.continueToCheckoutCurrency = product?.price?.currency || this.currency;

                if (product.isSingleTicketGroup) {
                    let totalCostOfSelectedTypes = 0;
                    for (const travellerType of product.userProfiles[0].companionProfiles) {
                        const code = travellerType.dynamicProductParameters[0].code;
                        const amountSelectedForType = product.dynamicProductParameters[code];
                        if (amountSelectedForType) {
                            totalCostOfSelectedTypes +=
                                amountSelectedForType *
                                (travellerType.perPersonPrice.amount ?? travellerType.perPersonPrice.maxAmount);
                        }
                    }
                    return totalCostOfSelectedTypes;
                }

                return product.amount * (product.price.amount ?? product.price.maxAmount);
            })
            .reduce((a, b) => a + b, 0);
    }

    nextButtonEnabled(): boolean {
        return this.purchaseService.getLocalProductAmount().some((product) => product.amount > 0);
    }
}
